import { highlightStringDifferences } from "../../../utils";
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState, useMemo } from "react";

const RECOMMENDATION_THRESHOLDS = {
  HIGH_CONFIDENCE: 0.9,
  MEDIUM_CONFIDENCE: 0.75,
};

const REJECT_GREY = "#757575";
const REJECT_GREY_DARK = "#616161";

const buildInitialMatchState = (matches) => {
  const state = {};
  matches.forEach((match) => {
    state[match.group_key] = true;
  });
  return state;
};

const getDifferenceTypeColor = (type) => {
  switch (type) {
    case "formatting":
      return "info";
    case "data_correction":
      return "warning";
    case "other":
      return "default";
    default:
      return "default";
  }
};

const getConfidenceColor = (confidence) => {
  if (confidence >= RECOMMENDATION_THRESHOLDS.HIGH_CONFIDENCE) return "success";
  if (confidence >= RECOMMENDATION_THRESHOLDS.MEDIUM_CONFIDENCE)
    return "warning";
  return "error";
};

const FieldDifference = ({ difference, isDuplicateMode = false }) => {
  return (
    <Box
      sx={{
        p: 2.5,
        bgcolor: "background.paper",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography
            variant={"h6"}
            sx={{
              fontWeight: 600,
              textTransform: "capitalize",
              mb: 0.5,
            }}
          >
            {difference.field_name.replace(/_/g, " ")}
          </Typography>
          <Chip
            label={difference.difference_type.replace(/_/g, " ")}
            size={"small"}
            color={getDifferenceTypeColor(difference.difference_type)}
            sx={{ textTransform: "capitalize" }}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: "#e3f2fd",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "info.light",
          }}
        >
          <Typography
            variant={"body2"}
            sx={{ fontWeight: 500, lineHeight: 1.6, color: "info.dark" }}
          >
            {difference.reasoning}
          </Typography>
        </Box>

        <TableContainer>
          <Table size={"small"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: "50%" }}>
                  {isDuplicateMode ? "Claim 1" : "Previous"}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: "50%" }}>
                  {isDuplicateMode ? "Claim 2" : "Current"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography
                    variant={"body2"}
                    sx={{ fontFamily: "monospace" }}
                  >
                    {highlightStringDifferences(
                      difference.previous_value,
                      difference.current_value,
                      true
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant={"body2"}
                    sx={{ fontFamily: "monospace" }}
                  >
                    {highlightStringDifferences(
                      difference.previous_value,
                      difference.current_value,
                      false
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Box>
  );
};

const ClaimDetailsTable = ({ group, allClaims, isDuplicateMode }) => {
  const allFields = new Set();

  allClaims.forEach((claim) => {
    if (claim) {
      Object.keys(claim).forEach((key) => {
        if (key !== "claim_id") {
          allFields.add(key);
        }
      });
    }
  });

  const differencesMap = {};
  group.field_differences.forEach((diff) => {
    differencesMap[diff.field_name] = diff;
  });

  const columnWidth =
    allClaims.length > 2 ? `${70 / allClaims.length}%` : "35%";

  return (
    <TableContainer
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
    >
      <Table size={"small"}>
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.50" }}>
            <TableCell sx={{ fontWeight: 700, width: "30%" }}>
              {"Field"}
            </TableCell>
            {allClaims.map((_, idx) => (
              <TableCell key={idx} sx={{ fontWeight: 700, width: columnWidth }}>
                {isDuplicateMode
                  ? `Claim ${idx + 1}`
                  : idx === 0
                  ? "Previous"
                  : "Current"}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from(allFields).map((fieldName) => {
            const hasDifference = differencesMap[fieldName];

            return (
              <TableRow key={fieldName} hover>
                <TableCell
                  sx={{ fontWeight: 600, textTransform: "capitalize" }}
                >
                  {fieldName.replace(/_/g, " ")}
                </TableCell>
                {allClaims.map((claim, idx) => {
                  const value = claim?.[fieldName];
                  const firstValue = allClaims[0]?.[fieldName];
                  const shouldHighlight = hasDifference && idx > 0;

                  return (
                    <TableCell
                      key={idx}
                      sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                    >
                      {shouldHighlight
                        ? highlightStringDifferences(firstValue, value, false)
                        : value || "-"}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const MatchedGroup = ({
  group,
  index,
  accepted,
  onAccept,
  onReject,
  priorClaimsMap,
  currentClaimsMap,
  isDuplicateMode = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isAccepted = accepted === true;
  const isRejected = accepted === false;

  const claimIds = group.claim_ids || group.current_claim_ids || [];
  const priorClaimIds = group.prior_claim_ids || [];

  const allClaims = isDuplicateMode
    ? claimIds.map((id) => currentClaimsMap?.[id]).filter(Boolean)
    : [
        priorClaimsMap?.[priorClaimIds[0]],
        currentClaimsMap?.[group.current_claim_ids?.[0]],
      ].filter(Boolean);

  const priorClaim = allClaims[0];
  const currentClaim = allClaims[1] || allClaims[0];

  const getClaimantName = () => {
    if (priorClaim?.claimant_name) {
      return priorClaim.claimant_name;
    }
    if (currentClaim?.claimant_name) {
      return currentClaim.claimant_name;
    }
    const nameDiff = group.field_differences.find(
      (d) => d.field_name === "claimant_name"
    );
    if (nameDiff) {
      if (isDuplicateMode) {
        return nameDiff.value_1 || nameDiff.value_2;
      } else {
        return nameDiff.current_value || nameDiff.previous_value;
      }
    }
    return "Unknown Claimant";
  };

  const getDate = () => {
    let dateStr = "";
    if (priorClaim?.date) {
      dateStr = priorClaim.date;
    } else if (currentClaim?.date) {
      dateStr = currentClaim.date;
    } else {
      const dateDiff = group.field_differences.find(
        (d) => d.field_name === "date"
      );
      if (dateDiff) {
        if (isDuplicateMode) {
          dateStr = dateDiff.value_1 || dateDiff.value_2;
        } else {
          dateStr = dateDiff.current_value || dateDiff.previous_value;
        }
      }
    }

    if (dateStr && dateStr.includes("T")) {
      return dateStr.split("T")[0];
    }
    return dateStr || "";
  };

  const getRecommendation = () => {
    const confidence = group.match_confidence;
    const differenceCount = group.field_differences.length;

    if (confidence >= RECOMMENDATION_THRESHOLDS.HIGH_CONFIDENCE) {
      return {
        action: "Accept",
        reason:
          differenceCount === 1
            ? "High confidence match with only minor formatting difference"
            : "High confidence match despite minor differences",
        color: "success",
        icon: <CheckCircleIcon />,
      };
    } else if (confidence >= RECOMMENDATION_THRESHOLDS.MEDIUM_CONFIDENCE) {
      return {
        action: "Review",
        reason:
          "Medium confidence - review differences carefully before accepting",
        color: "warning",
        icon: <VisibilityIcon />,
      };
    } else {
      return {
        action: "Reject",
        reason: "Low confidence - likely not the same claim",
        color: "error",
        icon: <CancelIcon />,
      };
    }
  };

  const claimTitle =
    isDuplicateMode && allClaims.length > 2
      ? `${index + 1}. ${getClaimantName()}: ${getDate()} (${
          allClaims.length
        } duplicate claims)`
      : `${index + 1}. ${getClaimantName()}: ${getDate()}`;
  const recommendation = getRecommendation();

  return (
    <Box
      sx={{
        mb: 2.5,
        borderRadius: 2,
        border: "2px solid",
        borderColor: isAccepted
          ? "primary.main"
          : isRejected
          ? REJECT_GREY
          : "grey.300",
        bgcolor: "background.paper",
        transition: "all 0.2s",
        "&:hover": {
          boxShadow: 3,
        },
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Typography variant={"h6"} sx={{ fontWeight: 600 }}>
                  {claimTitle}
                </Typography>
                <Chip
                  label={`${Math.round(
                    group.match_confidence * 100
                  )}% match confidence`}
                  size={"small"}
                  color={getConfidenceColor(group.match_confidence)}
                />
                {isAccepted && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={"Accepted"}
                    size={"small"}
                    color={"primary"}
                    sx={{
                      color: "white",
                      "& .MuiChip-icon": {
                        color: "white",
                      },
                    }}
                  />
                )}
                {isRejected && (
                  <Chip
                    icon={<CancelIcon />}
                    label={"Rejected"}
                    size={"small"}
                    sx={{
                      backgroundColor: REJECT_GREY,
                      color: "white",
                      "& .MuiChip-icon": {
                        color: "white",
                      },
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Chip
                  icon={recommendation.icon}
                  label={recommendation.action}
                  color={recommendation.color}
                  size={"small"}
                  sx={{
                    fontWeight: 600,
                  }}
                />
                <Typography variant={"body2"} color={"text.secondary"}>
                  {recommendation.reason}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant={isAccepted ? "contained" : "outlined"}
                color={"primary"}
                startIcon={<CheckCircleIcon />}
                onClick={onAccept}
                disabled={isAccepted}
                disableElevation
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "100px",
                  ...(!isAccepted && {
                    borderColor: "primary.main",
                    color: "primary.main",
                  }),
                }}
              >
                {"Accept"}
              </Button>
              <Button
                variant={isRejected ? "contained" : "outlined"}
                startIcon={<CancelIcon />}
                onClick={onReject}
                disabled={isRejected}
                disableElevation
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "100px",
                  borderColor: REJECT_GREY,
                  color: REJECT_GREY,
                  ...(isRejected && {
                    backgroundColor: REJECT_GREY,
                    color: "white",
                    "&:hover": {
                      backgroundColor: REJECT_GREY_DARK,
                    },
                  }),
                  ...(!isRejected && {
                    "&:hover": {
                      borderColor: REJECT_GREY_DARK,
                      backgroundColor: "rgba(117, 117, 117, 0.04)",
                    },
                  }),
                }}
              >
                {"Reject"}
              </Button>
            </Box>
          </Box>

          <Typography
            variant={"body1"}
            sx={{ fontWeight: 500, lineHeight: 1.7, color: "text.primary" }}
          >
            {isDuplicateMode
              ? group.overall_reasoning
              : group.overall_reasoning}
          </Typography>

          {group.field_differences.length > 0 && (
            <Box
              sx={{
                py: isExpanded ? 1.5 : 1,
                px: 2,
                bgcolor: "grey.50",
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: isExpanded ? 2 : 0,
                }}
              >
                <Typography
                  variant={"h6"}
                  sx={{ fontWeight: 600, fontSize: "1.1rem", lineHeight: 1 }}
                >
                  {"Difference Analysis"}
                </Typography>
                <IconButton
                  size={"small"}
                  sx={{
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Box>

              <Collapse in={isExpanded}>
                <Stack spacing={1.5} sx={{ pb: 0.5 }}>
                  {group.field_differences.map((diff, idx) => (
                    <FieldDifference
                      key={idx}
                      difference={diff}
                      isDuplicateMode={isDuplicateMode}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Box>
          )}

          <Button
            variant={"contained"}
            color={"primary"}
            size={"small"}
            startIcon={<VisibilityIcon />}
            onClick={() => setShowDetails(!showDetails)}
            disableElevation
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {`${showDetails ? "Hide" : "See all details"}`}
          </Button>

          <Collapse in={showDetails}>
            <ClaimDetailsTable
              group={group}
              allClaims={allClaims}
              isDuplicateMode={isDuplicateMode}
            />
          </Collapse>
        </Stack>
      </Box>
    </Box>
  );
};

const ClaimsMatcherModal = ({
  open,
  onClose,
  matchResults,
  onApply,
  priorClaims = [],
  currentClaims = [],
  initialAcceptedMatches = [],
  isDuplicateMode = false,
  title = "Claims Delta Analysis",
}) => {
  const [acceptedMatches, setAcceptedMatches] = useState(() =>
    buildInitialMatchState(initialAcceptedMatches)
  );

  const priorClaimsMap = useMemo(() => {
    const map = {};
    priorClaims.forEach((claim) => {
      if (claim.claim_id) {
        map[claim.claim_id] = claim;
      }
    });
    return map;
  }, [priorClaims]);

  const currentClaimsMap = useMemo(() => {
    const map = {};
    currentClaims.forEach((claim) => {
      if (claim.claim_id) {
        map[claim.claim_id] = claim;
      }
    });
    return map;
  }, [currentClaims]);

  const handleAccept = (groupKey) => {
    setAcceptedMatches((prev) => ({ ...prev, [groupKey]: true }));
  };

  const handleReject = (groupKey) => {
    setAcceptedMatches((prev) => ({ ...prev, [groupKey]: false }));
  };

  const setAllMatchesTo = (value) => {
    const allMatches = {};
    sortedResults.forEach((group) => {
      allMatches[group.group_key] = value;
    });
    setAcceptedMatches(allMatches);
  };

  const handleAcceptAll = () => setAllMatchesTo(true);
  const handleRejectAll = () => setAllMatchesTo(false);

  const handleApply = () => {
    const accepted = sortedResults.filter(
      (group) => acceptedMatches[group.group_key] === true
    );
    onApply(accepted);
    onClose();
  };

  const handleClose = () => {
    setAcceptedMatches(buildInitialMatchState(initialAcceptedMatches));
    onClose();
  };

  if (!matchResults) return null;

  const sortedResults = [...matchResults.results].sort((a, b) => {
    return (b.match_confidence || 0) - (a.match_confidence || 0);
  });

  const totalMatches = sortedResults.length;
  const acceptedCount = Object.values(acceptedMatches).filter(
    (val) => val === true
  ).length;
  const rejectedCount = Object.values(acceptedMatches).filter(
    (val) => val === false
  ).length;
  const pendingCount = totalMatches - acceptedCount - rejectedCount;

  const initialStateMap = buildInitialMatchState(initialAcceptedMatches);

  const hasChanges = sortedResults.some((group) => {
    const currentState = acceptedMatches[group.group_key];
    const initialState = initialStateMap[group.group_key];
    return currentState !== initialState;
  });

  const isEditMode = initialAcceptedMatches.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "95vw",
          maxWidth: "95vw",
          height: "92vh",
          maxHeight: "92vh",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant={"h5"} sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography
              variant={"body2"}
              color={"text.secondary"}
              sx={{ mt: 0.5 }}
            >
              {`${totalMatches} potential matches • `}
              <Box
                component={"span"}
                sx={{ color: "success.main", fontWeight: 600 }}
              >
                {`${acceptedCount} accepted`}
              </Box>
              {" • "}
              <Box
                component={"span"}
                sx={{ color: "error.main", fontWeight: 600 }}
              >
                {`${rejectedCount} rejected`}
              </Box>
              {" • "}
              <Box
                component={"span"}
                sx={{ color: "warning.main", fontWeight: 600 }}
              >
                {`${pendingCount} pending`}
              </Box>
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant={"contained"}
              color={"primary"}
              onClick={handleAcceptAll}
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {"Accept All"}
            </Button>
            <Button
              variant={"contained"}
              onClick={handleRejectAll}
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: REJECT_GREY,
                color: "white",
                "&:hover": {
                  backgroundColor: REJECT_GREY_DARK,
                },
              }}
            >
              {"Reject All"}
            </Button>
            <Divider orientation={"vertical"} flexItem sx={{ mx: 1 }} />
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        <Box>
          {sortedResults.map((group, index) => (
            <MatchedGroup
              key={group.group_key}
              group={group}
              index={index}
              accepted={acceptedMatches[group.group_key]}
              onAccept={() => handleAccept(group.group_key)}
              onReject={() => handleReject(group.group_key)}
              priorClaimsMap={priorClaimsMap}
              currentClaimsMap={currentClaimsMap}
              isDuplicateMode={isDuplicateMode}
            />
          ))}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          variant={"contained"}
          color={"secondary"}
          disableElevation
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {"Cancel"}
        </Button>
        <Button
          onClick={handleApply}
          variant={"contained"}
          color={"primary"}
          disabled={isEditMode ? !hasChanges : acceptedCount === 0}
          disableElevation
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {isEditMode
            ? hasChanges
              ? "Apply Changes"
              : "No changes to apply"
            : acceptedCount > 0
            ? `Apply ${acceptedCount} Match${acceptedCount !== 1 ? "es" : ""}`
            : "Select matches to apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClaimsMatcherModal;
